/**
 * Rate Limiting Handler - Обработка 429 ошибок с Retry-After
 * Следует лучшим практикам для обработки rate limiting
 */

import { logger } from './logger';

import { ref, computed } from 'vue';

export interface RateLimitInfo {
  retryAfter?: number;
  resetTime?: number;
  remainingRequests?: number;
  limit?: number;
}

export interface RateLimitState {
  isLimited: boolean;
  retryAfter?: number;
  resetTime?: number;
  waitTime?: number;
}

export class RateLimitHandler {
  private static instance: RateLimitHandler;
  private rateLimitState = ref<RateLimitState>({
    isLimited: false,
    retryAfter: undefined,
    resetTime: undefined,
    waitTime: undefined
  });
  
  private retryTimers = new Map<string, number>();
  private notifications = new Map<string, boolean>();

  public static getInstance(): RateLimitHandler {
    if (!RateLimitHandler.instance) {
      RateLimitHandler.instance = new RateLimitHandler();
    }
    return RateLimitHandler.instance;
  }

  /**
   * Проверка ответа на наличие rate limiting
   */
  public checkRateLimit(response: Response): RateLimitInfo | null {
    const retryAfter = response.headers.get('Retry-After');
    const remaining = response.headers.get('X-RateLimit-Remaining');
    const limit = response.headers.get('X-RateLimit-Limit');
    const reset = response.headers.get('X-RateLimit-Reset');

    if (response.status === 429 || retryAfter) {
      const retryAfterMs = this.parseRetryAfter(retryAfter);
      const resetTimeMs = reset ? parseInt(reset) * 1000 : Date.now() + retryAfterMs;

      return {
        retryAfter: retryAfterMs,
        resetTime: resetTimeMs,
        remainingRequests: remaining ? parseInt(remaining) : 0,
        limit: limit ? parseInt(limit) : undefined
      };
    }

    return null;
  }

  /**
   * Обработка rate limiting ошибки
   */
  public async handleRateLimit(
    endpoint: string, 
    rateLimitInfo: RateLimitInfo,
    onRetry?: () => Promise<void>
  ): Promise<void> {
    const key = endpoint;
    const waitTime = rateLimitInfo.retryAfter || 60000; // По умолчанию 1 минута

    // Обновляем состояние
    this.rateLimitState.value = {
      isLimited: true,
      retryAfter: rateLimitInfo.retryAfter,
      resetTime: rateLimitInfo.resetTime,
      waitTime
    };

    // Показываем уведомление пользователю
    this.showRateLimitNotification(endpoint, waitTime);

    // Очищаем предыдущий таймер
    this.clearRetryTimer(key);

    // Создаем новый таймер для повторной попытки
    return new Promise((resolve, reject) => {
      const timer = window.setTimeout(async () => {
        this.rateLimitState.value.isLimited = false;
        this.hideRateLimitNotification(key);

        try {
          if (onRetry) {
            await onRetry();
          }
          resolve();
        } catch (error) {
          reject(error);
        }
      }, waitTime);

      this.retryTimers.set(key, timer);
    });
  }

  /**
   * Получение реактивного состояния rate limiting
   */
  public getRateLimitState() {
    return computed(() => this.rateLimitState.value);
  }

  /**
   * Проверка, находится ли endpoint под rate limiting
   */
  public isRateLimited(endpoint?: string): boolean {
    if (endpoint) {
      return this.retryTimers.has(endpoint);
    }
    return this.rateLimitState.value.isLimited;
  }

  /**
   * Очистка всех таймеров
   * @deprecated Используйте cleanup() вместо этого метода
   */
  public clearAllTimers(): void {
    logger.warn('clearAllTimers is deprecated, use cleanup() instead');
    this.cleanup();
  }

  /**
   * Очистка всех таймеров и состояния
   */
  public cleanup(): void {
    this.retryTimers.forEach(timer => clearTimeout(timer));
    this.retryTimers.clear();
    this.rateLimitState.value.isLimited = false;
  }

  /**
   * Парсинг Retry-After заголовка
   */
  private parseRetryAfter(retryAfter: string | null): number {
    if (!retryAfter) return 60000;

    // Если это число в секундах
    const seconds = parseInt(retryAfter);
    if (!isNaN(seconds)) {
      return seconds * 1000;
    }

    // Если это HTTP дата
    const date = new Date(retryAfter);
    if (!isNaN(date.getTime())) {
      return date.getTime() - Date.now();
    }

    return 60000; // По умолчанию 1 минута
  }

  /**
   * Показать уведомление о rate limiting
   */
  private showRateLimitNotification(endpoint: string, waitTime: number): void {
    if (this.notifications.has(endpoint)) return;

    const message = `Слишком много запросов. Повторная попытка через ${Math.ceil(waitTime / 1000)} секунд.`;
    
    // Используем существующую систему уведомлений
    if ((window as any).messenger) {
      (window as any).messenger.notification(message, 'warning', 5000);
    } else if ((window as any).bootbox) {
      (window as any).bootbox.alert({
        message: message,
        backdrop: false,
        className: 'rate-limit-warning'
      });
    } else {
      logger.warn('Rate limit:', message);
    }

    this.notifications.set(endpoint, true);
  }

  /**
   * Скрыть уведомление о rate limiting
   */
  private hideRateLimitNotification(endpoint: string): void {
    this.notifications.delete(endpoint);
  }

  /**
   * Очистка таймера для конкретного endpoint
   */
  private clearRetryTimer(endpoint: string): void {
    const timer = this.retryTimers.get(endpoint);
    if (timer) {
      clearTimeout(timer);
      this.retryTimers.delete(endpoint);
    }
  }

  /**
   * Получение времени до следующей попытки
   */
  public getTimeToRetry(endpoint?: string): number {
    if (endpoint && this.retryTimers.has(endpoint)) {
      return this.rateLimitState.value.waitTime || 0;
    }
    return 0;
  }

  /**
   * Форматирование времени до следующей попытки
   */
  public formatTimeToRetry(ms: number): string {
    if (ms < 1000) return 'менее секунды';
    if (ms < 60000) return `${Math.ceil(ms / 1000)} секунд`;
    if (ms < 3600000) return `${Math.ceil(ms / 60000)} минут`;
    return `${Math.ceil(ms / 3600000)} часов`;
  }
}

// Экспорт для использования в composables
export const rateLimitHandler = RateLimitHandler.getInstance();

// Хук для Vue composables
export function useRateLimit() {
  const rateLimitState = rateLimitHandler.getRateLimitState();

  const checkAndHandleRateLimit = async (
    response: Response,
    endpoint: string,
    retryFn?: () => Promise<void>
  ): Promise<void> => {
    const rateLimitInfo = rateLimitHandler.checkRateLimit(response);
    if (rateLimitInfo) {
      await rateLimitHandler.handleRateLimit(endpoint, rateLimitInfo, retryFn);
      throw new Error(`Rate limited: ${rateLimitInfo.retryAfter}ms wait`);
    }
  };

  const isRateLimited = (endpoint?: string) => {
    return rateLimitHandler.isRateLimited(endpoint);
  };

  const getTimeToRetry = (endpoint?: string) => {
    return rateLimitHandler.getTimeToRetry(endpoint);
  };

  const formatTimeToRetry = (ms: number) => {
    return rateLimitHandler.formatTimeToRetry(ms);
  };

  return {
    rateLimitState,
    checkAndHandleRateLimit,
    isRateLimited,
    getTimeToRetry,
    formatTimeToRetry
  };
}
